
import BaseUiKit from '@rshb/rshb-vue-kit/src/components/base/BaseUiKit.vue'
import LazyHydrate from 'vue-lazy-hydration'
import { footerBottomMenu, getMenuTabs, firstFooterMenus, footerSocial, secondFooterMenus } from '~/shared/const/footer'

import Seo from '~/utils/seo.ts'
import Cookie from '@/components/Cookie/Cookie'
import RegionLocation from '@/components/RegionLocation/RegionLocation'
import ModalAuth from '~/components/Modal/ModalAuth.vue'
import BaseYavagroToastWrapper from '~/components/Notifications/BaseYavagroToastWrapper.vue'

export default {
  components: {
    RegionLocation,
    Cookie,
    LazyHydrate,
    BaseUiKit,
    ModalAuth,
    BaseYavagroToastWrapper,
    Header: () => import('~/components/Header/Header.vue'),
    Footer: () => import('~/components/Footer/Footer.vue'),
    BaseRabotaAgroBottomNavigationMenu: () =>
      import('~/components/BottomMobileMenu/BaseRabotaAgroBottomNavigationMenu.vue'),
    ModalContainer: () => import('~/components/Misc/ModalContainer.vue'),
    ModalRegionLocationContainer: () => import('~/components/Misc/ModalRegionLocationContainer.vue')
  },
  data() {
    return {
      headerPositon: 140,
      globalSearch: '',
      searchRegionValue: '',
      searchItems: [],
      showNoResults: false,
      windowWidth: 0,
      footerSocial,
      secondFooterMenus,
      footerBottomMenu,
      firstFooterMenus,
      observer: null
    }
  },

  head() {
    return Seo.getHeaderMeta(this.$tstore.pageMetadata.getSeoMetadata, this.$tstore.pageMetadata.getBreadcrumbs)
  },

  computed: {
    isLogged() {
      return this.$tstore.auth.isLogged
    },
    getCookie() {
      const matches = document.cookie.match(`(?:^|; )${this.ACCEPTED_COOKIE_NAME}=([^;]*)`)
      return !!matches
    },
    isShowTopBanner() {
      const path = this.$route.path
      const isSubscriptionPath =
        [
          '/digital-lecture',
          '/employers-tasks',
          '/research-showcase',
          '/students/ambassadors/employers-tasks'
        ].includes(path) || path.startsWith('/digital-lecture/')

      const isProfilePath = ['/', '/students', '/students/ambassadors'].includes(path)

      // Баннер подписки показываем всем
      if (isSubscriptionPath) return true

      // Баннер профиля — неавторизованным или физлицам без цифрового профиля
      if (isProfilePath) {
        if (!this.isLogged) return true
        const user = this.$tstore.auth.authorizedUser
        if (!user) return false
        return user.roles.has('fiz') && !user.isHaveDigitalProfile
      }

      return false
    },
    getCopyrightText() {
      return `© 2000-${new Date().getFullYear()} АО «Россельхозбанк»</br> Генеральная лицензия Банка России № 3349 от 12 августа 2015`
    },
    isShowHeader() {
      return !this.$tstore.layout.hideHeader
    },
    isShowFooter() {
      return !this.$tstore.layout.hideFooter
    },
    menuTabs() {
      return getMenuTabs(this.isLogged)
    }
  },
  watch: {
    async searchRegionValue() {
      if (this.searchRegionValue.length >= 3) {
        const regions = await this.$services.geolocation.getSearchRegions(this.searchRegionValue)

        if (Array.isArray(regions)) {
          this.$tstore.regionLocation.setRegionLocationList(regions)
        }
      } else {
        const regions = await this.$services.geolocation.getRegions()

        if (regions) {
          this.$tstore.regionLocation.setRegionLocationList(regions)
        }
      }
    },
    '$tstore.regionLocation.ShowModalRegionChange'() {
      if (this.$tstore.regionLocation.ShowModalRegionChange) {
        document.addEventListener('click', this.clickWithout)
      } else {
        document.removeEventListener('click', this.clickWithout)
      }
    }
  },

  mounted() {
    const el = document.querySelector('.header-container')

    if (el) {
      // Сохраняем observer в свойстве компонента
      this.observer = new IntersectionObserver(
        ([e]) => {
          e.target.classList.toggle('pinned', e.intersectionRatio < 1)
        },
        {
          threshold: [1]
        }
      )

      this.observer.observe(el)
    }

    if (
      // eslint-disable-next-line nuxt/no-env-in-hooks
      process.client &&
      !this.$services.geolocation.getCookieRegion() &&
      this.$tstore.regionLocation.RegionLocationItem &&
      this.getCookie
    ) {
      setTimeout(() => {
        if (window.innerWidth >= 1439) {
          this.$tstore.regionLocation.setIsShowModalRegionConfirm(true)
        } else {
          this.$tstore.regionLocation.setIsShowModalRegionConfirmMobile(true)
        }
      }, 10000)
    }

    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },

  beforeMount() {
    this.$tstore.pageHistory.setPageHistoryNumber(window.history.length)
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    menuTabClick(tab) {
      if (tab.openModal && !this.$tstore.auth.isLogged) {
        this.$tstore.auth.setShowModalAuth({ show: true })
      } else {
        this.$router.push(tab.ref)
      }
    },
    async onSearchInput(value) {
      this.globalSearch = value
      if (value.length > 2) {
        const vacancies = await this.getVacancies(value)
        const items = await this.$services.search.getSearch(value)
        delete items.courses

        this.searchItems =
          vacancies.length > 0
            ? {
                ...items,
                vacancies
              }
            : { ...items }
        if (!items.length) {
          this.showNoResults = true
        }
      } else {
        this.showNoResults = false
        this.searchItems = []
      }
    },

    extractSpecialnost(url) {
      const match = url.match(/specialnost-([^/?#]+)/)
      return match ? `specialnost-${match[1]}` : null
    },

    onClickRegionItem(region) {
      this.$services.geolocation.setCookieRegion(region)
      this.$tstore.regionLocation.setIsShowModalRegionChange(false)
      this.$tstore.regionLocation.setRegionLocationItem(region)

      const queryParams = { ...this.$route.query }
      delete queryParams.region
      delete queryParams['vacancies-cities']

      if (this.$route.name === 'looking-for-job-slug') {
        this.$router.push({ path: '/looking-for-job/' + region.slug })
      }

      if (this.$route.name.includes('vacancies') && !this.$route.params.id) {
        const slugSpecialnost = this.extractSpecialnost(this.$route.path)
        const basePath = region.slug === 'russia' ? '/vacancies' : `/vacancies/region-${region.slug}`

        const pathRoute = slugSpecialnost ? `${basePath}/${slugSpecialnost}` : basePath

        this.$router.push({ path: pathRoute, query: queryParams })
      }

      if (this.$route.name.includes('resume-list') && !this.$route.params.id) {
        const pathRoute = `/resume-list?filter[region]=${region.slug}`
        this.$router.push({ path: pathRoute, query: {} })
      }
    },

    closeModalRegion() {
      this.$tstore.regionLocation.setIsShowModalRegionConfirmMobile(false)
      this.$tstore.regionLocation.setIsShowModalRegionChange(false)
    },

    onSuccessAnswer(region) {
      this.$services.geolocation.setCookieRegion(region)
      this.$tstore.regionLocation.setRegionLocationItem(region)
      this.$tstore.regionLocation.setIsShowModalRegionConfirmMobile(false)

      if (this.$route.name === 'looking-for-job-slug' && region?.slug) {
        this.$router.push(`/looking-for-job/${region.slug}`)
      }

      if (this.$route.name.includes('vacancies')) {
        const pathRoute = region.slug === 'russia' ? '/vacancies' : `/vacancies/region-${region.slug}`
        this.$router.push(pathRoute)
      }
    },

    async onNegativeAnswer() {
      if (this.$tstore.regionLocation.RegionLocationList.length === 0) {
        const regions = await this.$services.geolocation.getRegions()
        if (regions) {
          this.$tstore.regionLocation.setRegionLocationList(regions)
        }
      }

      this.$tstore.regionLocation.setIsShowModalRegionConfirmMobile(false)
      this.$tstore.regionLocation.setIsShowModalRegionChange(true)
    },

    onUpdateValue(value) {
      this.searchRegionValue = value
    },

    async getVacancies(value) {
      const vacanciesList = await this.$services.requestRest.getList('vacancies', {
        'sf:position_name_kw': value,
        size: 5
      })
      return vacanciesList.data || []
    },

    clickWithout(e) {
      const el = this.$refs.regionChange
      const target = e.target
      if (target !== el && !el.contains(target)) {
        this.$tstore.regionLocation.setIsShowModalRegionChange(false)
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    }
  }
}
